import { history } from './history';
import { showToast } from '../services/toast';
import { logout as authLogout } from './authService';
import { store } from './store';
import { userConstants } from '../constants/user.constants';
import { clearFaroUserContext } from '../observability';

let sessionEndInProgress = false;

/**
 * Single place for session end: one toast, logout (clear storage + disconnect socket), Redux LOGOUT, redirect to login.
 * Guard prevents duplicate toast/redirect when multiple callers fire (e.g. N parallel 401s).
 * Guard resets after 2s so rapid duplicate calls still only produce one redirect.
 * Aligns with PP-GameApp pattern in common.js (handleSessionExpired).
 * @param {string} [_reason] - Optional: 'expired' | '401' for logging; not used for behavior.
 */
export function handleSessionEnd(_reason) {
    if (sessionEndInProgress) return;
    sessionEndInProgress = true;

    showToast('Session expired. Please login again.', 'error');
    clearFaroUserContext();
    authLogout();
    store.dispatch({ type: userConstants.LOGOUT });
    history.replace({ pathname: '/login', state: { sessionExpired: true } });

    setTimeout(() => {
        sessionEndInProgress = false;
    }, 2000);
}

/**
 * Parses NestJS 400 validation response format (class-validator).
 * Response shape: { ok: false, errors: { message: [ { property, constraints }, ... ], error, statusCode } }
 * @param {{ data?: { errors?: { message?: Array<{ property?: string, constraints?: Record<string, string> }> } } }} response - Axios response or response object with .data
 * @returns {{ message: string, validationErrors: Array<{ property: string, message: string }> } | null}
 */
export function getValidationMessageFromResponse(response) {
    const messageList = response?.data?.errors?.message;
    if (!Array.isArray(messageList) || messageList.length === 0) return null;
    const validationErrors = [];
    const parts = [];
    for (const item of messageList) {
        const constraints = item.constraints && typeof item.constraints === 'object' ? item.constraints : {};
        const messages = Object.values(constraints).filter(Boolean);
        const message = messages[0] || '';
        if (message) {
            parts.push(message);
            validationErrors.push({ property: item.property || '', message });
        }
    }
    if (parts.length === 0) return null;
    return { message: parts.join('. '), validationErrors };
}

export function handleResponse(response) {
    if (response.text) //Handle fetch() errors
    {
        return response.text().then(text => {
            const data = text && JSON.parse(text);

            if (!response.ok) {
                const error = (data && data.message) || response.statusText;
                return Promise.reject(error);
            }

            return data;
        });
    }
    else if (!response) {

        localStorage.removeItem('user'); //logout
        // @ts-ignore
        window.location.reload(true);

    } else { //Handle axios() errors

        const status = Number(response?.status || 0);
        if (status >= 200 && status < 300) {
            const { data = [] } = response;
            const businessMessage = data?.responseMessage || data?.errorMessage || data?.message;
            const isBusinessError = data && (
                data.ok === false ||
                (typeof businessMessage === 'string' && /already exists|already in use|related records exist|unable to delete the user|duplicate/i.test(businessMessage))
            );

            if (data && data.message) {
                if (isBusinessError) {
                    const err = new Error(businessMessage || response.statusText || 'Request failed');
                    err.errorCode = data.errorCode;
                    err.errorMessage = businessMessage || response.statusText || 'Request failed';
                    return Promise.reject(err);
                }
                return Promise.reject((data && data.message) || response.statusText);
            }
            if (isBusinessError) {
                const err = new Error(businessMessage || response.statusText || 'Request failed');
                err.errorCode = data.errorCode;
                err.errorMessage = businessMessage || response.statusText || 'Request failed';
                return Promise.reject(err);
            }
            return response.data;
        }

        const validation = getValidationMessageFromResponse(response);
        if (validation) {
            const err = new Error(validation.message);
            err.validationErrors = validation.validationErrors;
            err.alreadyShownToast = true;
            return Promise.reject(err);
        }
        const fallback = (response.data && (response.data.errorMessage || response.data.message)) || response.statusText;
        const err = new Error(typeof fallback === 'string' ? fallback : response.statusText || 'Request failed');
        return Promise.reject(err);
    }
}

/**
 * Builds a rejection from an API error response.
 * 401/403/5xx and network errors are handled by axiosInstance interceptors; this is for other (e.g. 400) responses.
 * For network errors (no response), the axios interceptor already shows a toast.
 * Reject with a flag so callers don't show a second toast.
 */
export function handleError(response) {
    const directError = response instanceof Error ? response : null;
    if (directError) {
        if (!directError.response && !(directError.errorMessage || directError.message)) {
            const err = new Error('Network error. Please check your connection and try again.');
            err.alreadyShownToast = true;
            return Promise.reject(err);
        }
        const err = new Error(directError.errorMessage || directError.message || 'Request failed');
        err.errorCode = directError.errorCode;
        err.errorMessage = directError.errorMessage || directError.message || 'Request failed';
        err.alreadyShownToast = directError.alreadyShownToast || false;
        return Promise.reject(err);
    }

    if (!response) {
        const err = new Error('Network error. Please check your connection and try again.');
        err.alreadyShownToast = true;
        return Promise.reject(err);
    }
    const validation = getValidationMessageFromResponse(response);
    if (validation) {
        const err = new Error(validation.message);
        err.validationErrors = validation.validationErrors;
        err.alreadyShownToast = true;
        return Promise.reject(err);
    }
    if (response && response.data && response.data.ok === false) {
        const err = new Error(response.data.errorMessage || response.statusText || 'Request failed');
        err.errorCode = response.data.errorCode;
        err.errorMessage = response.data.errorMessage || response.statusText || 'Request failed';
        return Promise.reject(err);
    }
    const fallback = (response.data && response.data.errorMessage) || (response.data && response.data.message) || response.statusText;
    const err = new Error(typeof fallback === 'string' ? fallback : response.statusText || 'Request failed');
    err.errorCode = response.data && response.data.errorCode;
    err.errorMessage = response.data && response.data.errorMessage ? response.data.errorMessage : (typeof fallback === 'string' ? fallback : response.statusText || 'Request failed');
    return Promise.reject(err);
}
